import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { DisplayFieldFeedback as DisplayFieldFeedbackType } from "@/components/Atoms/DisplayField/display-field.shared";
import { SET_LOCATION_MAX_GEOCODE_RESULTS } from "@/components/Organisms/SetLocationModal/SetLocationModalBody";
import { Input } from "@/components/base/input/input";
import { useDebounced } from "@/hooks/use-debounced";
import { geocodeSuggestQueryOptions } from "@/services/map/geocode.options";
import { DEFAULT_GEOCODE_CONFIG } from "@/types/config.types";

export interface AddressSelection {
	text: string;
}

/** Suggestion text only. No coordinates and no geometry. */
export function addressSelectionFromSuggestion(suggestion: {
	primary: string;
}): AddressSelection {
	return { text: suggestion.primary };
}

export interface AddressPickerProps {
	value: string;
	onChange: (value: string) => void;
	onBlur?: () => void;
	onFocus?: () => void;
	onKeyDown?: (event: {
		key: string;
		preventDefault: () => void;
		stopPropagation: () => void;
	}) => void;
	/** When false, the field stays free text and does not request suggestions. */
	suggestionsEnabled?: boolean;
	label?: string;
	placeholder?: string;
	isDisabled?: boolean;
	isRequired?: boolean;
	isInvalid?: boolean;
	hint?: string;
	feedback?: DisplayFieldFeedbackType;
}

export function AddressPicker({
	value,
	onChange,
	onBlur,
	onFocus,
	onKeyDown,
	suggestionsEnabled = true,
	label,
	placeholder = "Search or enter an address",
	isDisabled,
	isRequired,
	isInvalid,
	hint,
	feedback,
}: AddressPickerProps) {
	const [suggestionsOpen, setSuggestionsOpen] = useState(false);
	const debounced = useDebounced(value, 300);
	const queryText = debounced.trim();
	const suggest = useQuery({
		...geocodeSuggestQueryOptions({
			request: {
				address: queryText,
				maxSuggestions: SET_LOCATION_MAX_GEOCODE_RESULTS,
				countryCode: DEFAULT_GEOCODE_CONFIG.countryCode,
			},
			enabled:
				suggestionsEnabled &&
				!isDisabled &&
				queryText.length >= DEFAULT_GEOCODE_CONFIG.minQueryLength,
		}),
	});
	const suggestions = (suggest.data ?? []).slice(
		0,
		SET_LOCATION_MAX_GEOCODE_RESULTS,
	);

	return (
		<div className="flex flex-col gap-1">
			<Input
				label={label}
				isRequired={isRequired}
				isDisabled={isDisabled}
				isInvalid={isInvalid}
				hint={hint}
				feedback={feedback}
				placeholder={placeholder}
				value={value}
				onChange={(next) => {
					setSuggestionsOpen(true);
					onChange(typeof next === "string" ? next : "");
				}}
				onBlur={() => {
					setSuggestionsOpen(false);
					onBlur?.();
				}}
				onFocus={onFocus}
				onKeyDown={(event) => {
					onKeyDown?.(event);
					if (event.key === "Enter") {
						event.preventDefault();
						setSuggestionsOpen(false);
						onBlur?.();
					}
				}}
			/>
			{suggestionsEnabled && suggestionsOpen && suggestions.length > 0 ? (
				<ul className="flex flex-col rounded-lg bg-primary shadow-xs ring-1 ring-primary ring-inset">
					{suggestions.map((suggestion) => (
						<li key={suggestion.id}>
							<button
								type="button"
								className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-primary_hover"
								onMouseDown={(event) => {
									event.preventDefault();
								}}
								onClick={() => {
									setSuggestionsOpen(false);
									onChange(addressSelectionFromSuggestion(suggestion).text);
									onBlur?.();
								}}
							>
								{suggestion.primary}
							</button>
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
}
